import React, { useCallback } from "react";
import { IDMobile } from "../../../components/IDs/IDs";
import ReactTooltip from "react-tooltip";
import { FiltersDropDown } from "../../../components/FiltersDropDown/FiltersDropDown";
import { TablePageLayout } from "../../../components/TablePage/TablePageLayout";
import type {
  IOrderItem,
  IOrdersAPIResponse,
  IPurchaseOrder,
  OrderStatus,
  StatusAndId,
} from "../../../types/types";
import { PageWrapper } from "../../../layout/portalPageLayout";
import {
  DEFAULT_PER_PAGE,
  formatAddressCityState,
  formatDate,
  formatDateTime,
  useInAppNotifications,
  useStoreState,
} from "../../../util/util";
import { useRoutePath } from "../../../util/Routing";
import { useAuthenticatedUser } from "../../../hooks/useAuthenticatedUser";
import { getOrderItemsList } from "../../../util/QuotesAndOrders";
import { useTranslation } from "react-i18next";
import { calculatePriceDetails } from "../../../components/PriceDetails/PriceDetails";
import { TooltipCell, update_status_string_list } from "./BuyerOrders.util";
import { TrimmedName } from "../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/AssetsUtil";
import type { Getter } from "@tanstack/react-table";
import {
  BuyerOrderStatus,
  getBuyerOrderStatusFilters,
  getOrderProductsAsText,
} from "../../../components/OrderStatus/OrderStatus";
import { useListPage } from "../../SharedPages/shared";

type OrdersTable = {
  id: string;
  products: { items: IOrderItem[]; id: string };
  seller_name: string;
  total_value: string;
  last_activity: string;
  status: StatusAndId<OrderStatus>;
  allOrderItems: IOrderItem[];
  unread_message_count?: number;
};

export const BuyerOrders = () => {
  const user = useAuthenticatedUser();
  const { t } = useTranslation();
  const { storefront_id } = useStoreState();
  const { ordersNotifications } = useInAppNotifications(storefront_id, user);
  const { accountPath } = useRoutePath();

  const columns = [
    {
      header: t("Order ID"),
      accessorKey: "number",
      id: "number",
      cell: ({ getValue }: { getValue: Getter<string> }) => (
        <IDMobile>{`${getValue()}`}</IDMobile>
      ),
    },
    {
      header: t("Product(s)"),
      accessorKey: "products",
      id: "products",
      cell: ({
        getValue,
      }: {
        getValue: Getter<{ items: IOrderItem[]; id: string }>;
      }) => (
        <TooltipCell data-for={`${getValue().id}-tooltip`} data-tip={""}>
          <TrimmedName text={getOrderProductsAsText(getValue().items, t)} />
          <ReactTooltip
            id={`${getValue().id}-tooltip`}
            place="top"
            data-html={true}
            effect="solid"
            backgroundColor="#60676F"
            multiline={true}
          >
            {getOrderItemsList(getValue().items)}
          </ReactTooltip>
        </TooltipCell>
      ),
    },
    {
      header: t("Seller"),
      accessorKey: "seller_name",
      id: "seller_name",
    },
    {
      header: t("Total Value"),
      accessorKey: "total_value",
      id: "total_value",
    },
    {
      header: t("Last Activity"),
      accessorKey: "last_activity",
      id: "last_activity",
      align: "right",
    },
    {
      header: t("Order Placed"),
      accessorKey: "created_at",
      id: "created_at",
      align: "right",
    },
    { header: t("Shipping Address"), accessorKey: "ship_to", id: "ship_to" },
    {
      header: t("Status"),
      accessorKey: "status",
      id: "status",
      size: 50,
      minSize: 50,
      align: "right",
      cell: ({ getValue }: { getValue: Getter<StatusAndId<OrderStatus>> }) => (
        <BuyerOrderStatus t={t} status={getValue().status} id={getValue().id} />
      ),
    },
  ];

  const dataTransformer = useCallback(
    (response: IOrdersAPIResponse): OrdersTable[] =>
      response.data.map((order: IPurchaseOrder) => ({
        id: order.id,
        number: order.number,
        products: { items: order.items, id: order.id },
        seller_name: order.seller_name || "--",
        total_value: (() => {
          const maybeInvoiceCharges =
            order.invoice && order.invoice.additional_charges
              ? order.invoice.additional_charges
              : [];
          const feesAndCharges = [...order.fees, ...maybeInvoiceCharges];
          const { totalString } = calculatePriceDetails({
            items: order.items,
            fees: feesAndCharges,
            totalDiscount: order.total_discounts,
            currencyCode: order.currency,
          });
          return totalString;
        })(),
        last_activity: formatDateTime(order.modified_at) || "--",
        created_at: formatDate(order.created_at),
        ship_to: formatAddressCityState(order.shipping_address),
        status: { status: order.status || "--", id: order.id },
        allOrderItems: order.items,
        unread_message_count: order.unread_message_count,
      })),
    []
  );

  const paginationTransformer = useCallback(
    (response: IOrdersAPIResponse) => ({
      total: response.pagination.total,
      offset: response.pagination.offset,
    }),
    []
  );

  const getUnreadIds = useCallback(
    (order: OrdersTable) =>
      ordersNotifications?.ids
        ? ordersNotifications.ids.includes(order.id)
        : false,
    [ordersNotifications]
  );

  const {
    state,
    data: tableData,
    isLoading,
    error,
    handlers,
    tableColumns,
  } = useListPage<OrdersTable, IOrdersAPIResponse>({
    defaultPerPage: DEFAULT_PER_PAGE,
    perPageOptions: [10, 20, 50, 100],
    endpoint: `/v1/storefronts/${storefront_id}/orders`,
    requestParams: { order_by: "desc" },
    dataTransformer,
    paginationTransformer,
    statusTransformer: update_status_string_list,
    getUnreadIds,
    notifications: ordersNotifications,
    statusFilters: getBuyerOrderStatusFilters(t),
    accountManagerFilters: [],
    customerServiceRepFilters: [],
    columns,
    custom_columns: { unread_message_count: 5 },
    basePath: `${accountPath}/orders`,
    searchPlaceholder: t("Search by Product or ID"),
  });

  const handleRowClick = (
    e: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => {
    handlers.handleRowClick(e, e.currentTarget.id, `${accountPath}/orders`);
  };

  return (
    <PageWrapper>
      <TablePageLayout
        showTitle
        title={t("Orders")}
        searchQuery={state.searchQuery}
        searchPlaceholder={t("Search by Product or ID")}
        onSearchChange={handlers.handleSearch}
        onSearchClear={handlers.handleClearSearch}
        filtersSlot={
          <FiltersDropDown
            activeItems={state.selectedStatus}
            applyStatus={handlers.applyStatus}
            list={getBuyerOrderStatusFilters(t)}
          />
        }
        perPage={state.perPage}
        perPageItems={state.perPageItems}
        onPerPageChange={handlers.changePerPage}
        columns={tableColumns}
        data={tableData}
        isLoading={isLoading}
        error={error}
        rowClick={handleRowClick}
        pagination={state.tablePagination}
        totalCount={state.tablePagination.totalCount}
        offset={state.offset}
        onPageChange={handlers.changePage}
      />
    </PageWrapper>
  );
};
